
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ImageUploadModal from '../media/ImageUploadModal';
import CameraComponent from '../media/CameraComponent';
import AppFooter from '../AppFooter';
import { FaCamera, FaUpload, FaTimes, FaPlus, FaList, FaArrowLeft } from 'react-icons/fa';
// import MediaPicker from '../media/MediaPicker';

function MemberAdd() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [registerStatus, setRegisterStatus] = useState('');
  const [waiverUrl, setWaiverUrl] = useState('');
  const [imagePath, setImagePath] = useState(null);          // preview URL (camera or picker)
  const [cameraImageUrl, setCameraImageUrl] = useState(null); // absolute or relative URL returned by server after camera upload
  const [showCamera, setShowCamera] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);     // from ImageUploadModal

  const navigate = useNavigate();
  const tenant = useParams().tenant || localStorage.getItem('tenantId');
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    const fetchWaiverUrl = async () => {
      try {
        const response = await axios.get('/api/tenant-waiver');
        setWaiverUrl(response.data.waiver || '');
      } catch (error) {
        console.error('Error fetching waiver URL:', error);
      }
    };
    fetchWaiverUrl();
  }, [tenant]);

  // For ImageUploadModal
  const handleUploadComplete = (files) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setImagePath(URL.createObjectURL(files[0]));
      setCameraImageUrl(null); // prefer the newly picked file
    }
    setIsImageModalOpen(false);
  };

  const handleCancel = () => navigate(`/${tenant}/members`);

  // Use the camera component's custom uploader to post to your existing route
  const uploadMemberCamera = async (formData) => {
    // formData has: file (blob)
    const res = await axios.post(
      `${baseUrl}/api/${tenant}/uploadmember`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
    );
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Camera upload failed');
    }
    // Normalize a response with absolute URL for preview/registration
    const absolute = res.data.url || (res.data.filePath ? `${baseUrl}${res.data.filePath}` : null);
    return { url: absolute, path: res.data.filePath };
  };

  // Called by CameraComponent after its uploader finishes
  const handleCameraSaved = (saved) => {
    const url = saved?.url || saved?.path;
    if (url) {
      setCameraImageUrl(url);  // store to send with register
      setImagePath(url);       // preview
      setSelectedFile(null);   // ensure we don't double-upload
    }
  };

  // Register member (uploads picker file if present; otherwise uses camera URL if present)
  const registerMember = async (e) => {
    e.preventDefault();
    setRegisterStatus('');

    let uploadedImageUrl = cameraImageUrl || null;

    // If user chose an image via modal, upload it now (kept from your original flow)
    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      try {
        const uploadResponse = await axios.post(
          `${baseUrl}/api/${tenant}/uploadmember`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
        );
        if (uploadResponse.data?.success) {
          uploadedImageUrl =
            uploadResponse.data.url ||
            (uploadResponse.data.filePath ? `${baseUrl}${uploadResponse.data.filePath}` : null);
        }
      } catch {
        console.warn('Image upload failed, proceeding without an image.');
      }
    }

    // Proceed with registration
    try {
      const response = await axios.post(
        `${baseUrl}/api/members/${tenant}/registermember`,
        {
          email,
          firstname: firstName,
          lastname: lastName,
          address1,
          address2,
          city,
          state,
          zip,
          phone,
          image: uploadedImageUrl || null, // can be null
        },
        { withCredentials: true }
      );

      if (response.data?.success) {
        navigate(`/${tenant}/members`);
      } else {
        console.error('Registration failed:', response.data?.message);
        setRegisterStatus(response.data?.message || 'Registration failed.');
      }
    } catch (error) {
      console.error('Error registering member:', error);
      setRegisterStatus('Error registering member. Please try again.');
    }
  };

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">Create Account</h2>
      <form onSubmit={registerMember}>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input type="text" className="form-control" id="firstName" name="firstName"
                 value={firstName} onChange={(e) => setFirstName(e.target.value)}
                 placeholder="First Name" required />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input type="text" className="form-control" id="lastName" name="lastName"
                 value={lastName} onChange={(e) => setLastName(e.target.value)}
                 placeholder="Last Name" required />
        </div>

        <div className="form-group">
          <label htmlFor="address1">Address 1:</label>
          <input type="text" className="form-control" id="address1" name="address1"
                 value={address1} onChange={(e) => setAddress1(e.target.value)}
                 placeholder="Address 1" required />
        </div>

        <div className="form-group">
          <label htmlFor="address2">Address 2:</label>
          <input type="text" className="form-control" id="address2" name="address2"
                 value={address2} onChange={(e) => setAddress2(e.target.value)}
                 placeholder="Address 2" />
        </div>

        <div className="form-group">
          <label htmlFor="city">City:</label>
          <input type="text" className="form-control" id="city" name="city"
                 value={city} onChange={(e) => setCity(e.target.value)}
                 placeholder="City" required />
        </div>

        <div className="form-group">
          <label htmlFor="state">State:</label>
          <input type="text" className="form-control" id="state" name="state"
                 value={state} onChange={(e) => setState(e.target.value)}
                 placeholder="State" required />
        </div>

        <div className="form-group">
          <label htmlFor="zip">Zip:</label>
          <input type="text" className="form-control" id="zip" name="zip"
                 value={zip} onChange={(e) => setZip(e.target.value)}
                 placeholder="Zip" required />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address:</label>
          <input type="email" className="form-control" id="email" name="email"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="Enter your Email Address" required />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input type="text" className="form-control" id="phone" name="phone"
                 value={phone} onChange={(e) => setPhone(e.target.value)}
                 placeholder="Enter your Phone Number" required />
        </div>

        {imagePath && (
          <div className="preview-container" style={{ display: 'flex', flexWrap: 'wrap', marginTop: 10 }}>
            <img src={imagePath} alt="Preview" style={{ width: 100, marginRight: 5, marginBottom: 5 }} />
          </div>
        )}
      </form>

      {registerStatus && <p className="status-message">{registerStatus}</p>}

      {showCamera && (
        <CameraComponent
          tenantId={Number(tenant)}
          onUpload={uploadMemberCamera}
          onSaved={handleCameraSaved}
          onClose={() => setShowCamera(false)}
        />
      )}

      {isImageModalOpen && (
        <ImageUploadModal
          tenantId={tenant}
          imageName={`${Date.now()}_${tenant}_${firstName}${lastName}`}
          onUploadComplete={handleUploadComplete}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Cancel', onClick: () => navigate(-1) },
          { icon: <FaList />, label: 'Members', onClick: handleCancel },
          { icon: <FaCamera />, label: 'Take Photo', onClick: () => setShowCamera(true), disabled: false },
          { icon: <FaUpload />, label: 'Upload Photo', onClick: () => setIsImageModalOpen(true), disabled: false },
          { icon: <FaPlus />, label: 'Create', onClick: registerMember, type: 'submit' },
        ]}
      />
    </div>
  );
}

export default MemberAdd;
